import { Resource } from '@domain/base/Resource';
import { IAuthRepository } from '@domain/account/IAuthRepository';
import { Session } from '@domain/account/models/Session';
import { Token } from '@domain/account/models/Token';
import { User } from '@domain/account/models/User';
import { LastLoginMethod } from '@domain/account/models/LastLoginMethod';
import { AuthDataSource } from '../datasources/AuthDataSource';
import { AuthMapper } from '../mappers/AuthMapper';

export class AuthRepository implements IAuthRepository {
    constructor(private dataSource: AuthDataSource) { }

    async getLastLoginMethod(email: string): Promise<Resource<LastLoginMethod>> {
        try {
            const data = await this.dataSource.getLastLoginMethod(email);
            return {
                status: 'success',
                data: AuthMapper.toLastLoginMethodModel(data)
            };
        } catch (error) {
            return {
                status: 'error',
                error: {
                    type: 'UnknownError',
                    message: 'Failed to get last login method'
                }
            };
        }
    }

    async userExists(email: string): Promise<Resource<boolean>> {
        try {
            const exists = await this.dataSource.userExists(email);
            return { status: 'success', data: exists };
        } catch (error) {
            return {
                status: 'error',
                error: {
                    type: 'UnknownError',
                    message: 'Failed to check if user exists'
                }
            };
        }
    }

    async sendCode(email: string): Promise<Resource<Token>> {
        try {
            const data = await this.dataSource.sendCode(email);
            return {
                status: 'success',
                data: AuthMapper.toTokenModel(data)
            };
        } catch (error) {
            return {
                status: 'error',
                error: {
                    type: 'UnknownError',
                    message: 'Failed to send verification code'
                }
            };
        }
    }

    async verifyCode(userId: string, code: string): Promise<Resource<Session>> {
        try {
            const data = await this.dataSource.verifyCode(userId, code);
            return {
                status: 'success',
                data: AuthMapper.toSessionModel(data)
            };
        } catch (error) {
            return {
                status: 'error',
                error: {
                    type: 'UnknownError',
                    message: 'Failed to verify code'
                }
            };
        }
    }

    async verifyPassword(email: string, password: string): Promise<Resource<Session>> {
        try {
            const data = await this.dataSource.verifyPassword(email, password);
            return {
                status: 'success',
                data: AuthMapper.toSessionModel(data)
            };
        } catch (error) {
            return {
                status: 'error',
                error: {
                    type: 'UnknownError',
                    message: 'Failed to verify password'
                }
            };
        }
    }

    async createJwt(): Promise<Resource<string>> {
        try {
            const data = await this.dataSource.createJwt();
            return { status: 'success', data: data };
        } catch (error) {
            return { status: 'error', error: { type: 'UnknownError', message: 'Failed to get JWT' } };
        }
    }

    async registerUser(
        email: string,
        name: string,
        companyName: string,
        termsAccepted: boolean,
        password?: string,
        discountCode?: string,
        billingProvider?: 'stripe' | 'shopify',
        referralCode?: string,
    ): Promise<Resource<User>> {
        try {
            const data = await this.dataSource.registerUser({
                email,
                name,
                companyName,
                termsAccepted,
                password,
                discountCode,
                referralCode,
                billingProvider,
                type: 'email',
            });
            return {
                status: 'success',
                data: AuthMapper.toUserModel(data)
            };
        } catch (error) {
            return {
                status: 'error',
                error: {
                    type: 'UnknownError',
                    message: 'Failed to register user'
                }
            };
        }
    }

    async getCurrentSession(): Promise<Resource<Session>> {
        try {
            const data = await this.dataSource.getCurrentSession();
            return {
                status: 'success',
                data: AuthMapper.toSessionModel(data)
            };
        } catch (error) {
            return {
                status: 'error',
                error: {
                    type: 'UnknownError',
                    message: 'Failed to get current session'
                }
            };
        }
    }

    async getCurrentUser(): Promise<Resource<User>> {
        try {
            const data = await this.dataSource.getCurrentUser();
            return {
                status: 'success',
                data: AuthMapper.toUserModel(data)
            };
        } catch (error) {
            return {
                status: 'error',
                error: {
                    type: 'UnknownError',
                    message: 'Failed to get current user'
                }
            };
        }
    }

    async isUserLoggedIn(): Promise<Resource<boolean>> {
        try {
            const isLoggedIn = await this.dataSource.isUserLoggedIn();
            return { status: 'success', data: isLoggedIn };
        } catch (error) {
            return {
                status: 'error',
                error: {
                    type: 'UnknownError',
                    message: 'Failed to check login status'
                }
            };
        }
    }

    async logout(): Promise<Resource<void>> {
        try {
            await this.dataSource.logout();
            return { status: 'success', data: undefined };
        } catch (error) {
            return {
                status: 'error',
                error: {
                    type: 'UnknownError',
                    message: 'Failed to logout'
                }
            };
        }
    }

    async listUsers(search?: string, subscriptionStatuses?: string[]): Promise<Resource<User[]>> {
        try {
            const data = await this.dataSource.listUsers(search, subscriptionStatuses);
            return {
                status: 'success',
                data: data.map((user) => AuthMapper.toUserModel(user)),
            };
        } catch {
            return {
                status: 'error',
                error: {
                    type: 'UnknownError',
                    message: 'Failed to list users',
                },
            };
        }
    }

    prefetchUsers(): void {
        this.dataSource.prefetchUsers();
    }

    async hasImpersonatorAccess(): Promise<boolean> {
        try {
            return await this.dataSource.hasImpersonatorAccess();
        } catch {
            return false;
        }
    }
}